import {
  CompareMetadata,
  CompareTableParams,
  entriesToCompareMetadata,
  entriesToIdsOrNames,
} from '@neptune/compare-domain';
import { SearchQueryModelConverter } from '@neptune/search-query-domain';
import {
  GroupByModelConverter,
  LeaderboardEntry,
  LeaderboardQueryNameAliases,
  searchLeaderboard,
  SortOptionsModelConverter,
} from '@neptune/shared/leaderboard-domain';

import {
  KnownAttributes,
  knownAttributesForExperimentIdentification,
} from 'domain/experiment/attribute';

function enrichQueryWithUnarchivedOnly(inputQuery: string | undefined) {
  return SearchQueryModelConverter.convertSearchQueryToNql({
    operator: 'and',
    criteria: [
      SearchQueryModelConverter.convertNqlToSearchQuery(inputQuery),
      {
        attribute: KnownAttributes.Archived,
        operator: '=',
        type: 'bool',
        value: false,
      },
    ],
  });
}

export async function resolveAutoCompareEntries(
  projectIdentifier: string,
  tableParams: CompareTableParams,
  autoComparePool: number,
  queryNameAliases?: LeaderboardQueryNameAliases,
): Promise<LeaderboardEntry[]> {
  const leaderboardResult = await searchLeaderboard({
    attributesToFetch: [...knownAttributesForExperimentIdentification],
    projectIdentifier,
    type: ['run'],
    experimentsOnly: tableParams.experimentsOnly,
    queryNameAliases,
    grouping: tableParams.groupOptions?.groupBy?.length
      ? {
          groupBy: GroupByModelConverter.fromGroupOptionsToDomain(tableParams.groupOptions),
          openedGroups: [],
        }
      : undefined,
    pagination: {
      limit: autoComparePool,
      offset: 0,
    },
    query: enrichQueryWithUnarchivedOnly(tableParams.query),
    sorting: tableParams.sortOptions
      ? SortOptionsModelConverter.fromLegacyToDomain(tableParams.sortOptions)
      : undefined,
  });

  return leaderboardResult.entries;
}

export async function resolveAutoCompareRunIdsAndExperimentNames(
  projectIdentifier: string,
  tableParams: CompareTableParams,
  autoComparePool: number,
): Promise<{
  metadata: CompareMetadata;
  identificationKeys: { runIds: Set<string>; experimentNames: Set<string> };
}> {
  const entries = await resolveAutoCompareEntries(projectIdentifier, tableParams, autoComparePool);

  const identificationKeys = entriesToIdsOrNames(entries, tableParams.experimentsOnly);
  const metadata = entriesToCompareMetadata(entries);

  return {
    metadata,
    identificationKeys,
  };
}

export async function resolveAutoCompareRunIdsAndExperimentNamesArrays(
  projectIdentifier: string,
  tableParams: CompareTableParams,
  autoComparePool: number,
  queryNameAliases?: LeaderboardQueryNameAliases,
): Promise<{
  metadata: CompareMetadata;
  identificationKeys: { runIds: string[]; experimentNames: string[] };
}> {
  const entries = await resolveAutoCompareEntries(
    projectIdentifier,
    tableParams,
    autoComparePool,
    queryNameAliases,
  );

  const metadata = entriesToCompareMetadata(entries);
  const identificationKeys = entriesToIdsOrNames(entries, tableParams.experimentsOnly, Array.from);

  return {
    metadata,
    identificationKeys,
  };
}
